import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Check, Loader2, PawPrint, PartyPopper } from "lucide-react";
import services from "@/data/services.json";

const PERIODS = [
    { id: "manha", label: "Manhã" },
    { id: "tarde", label: "Tarde" },
];

export default function BookingModal({ open, onOpenChange, initialService }) {
    const [petName, setPetName] = useState("");
    const [petType, setPetType] = useState("cao");
    const [serviceId, setServiceId] = useState(initialService || services[0].id);
    const [date, setDate] = useState("");
    const [period, setPeriod] = useState("manha");
    const [error, setError] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    useEffect(() => {
        if (open) {
            if (initialService) setServiceId(initialService);
            setSent(false);
            setSending(false);
            setError("");
        }
    }, [open, initialService]);

    const today = new Date().toISOString().split("T")[0];

    const submit = (e) => {
        e.preventDefault();
        if (!petName.trim()) {
            setError("Diga o nome do pet para a demonstração.");
            return;
        }
        if (!date) {
            setError("Escolha uma data para a demonstração.");
            return;
        }
        setError("");
        setSending(true);
        setTimeout(() => {
            setSending(false);
            setSent(true);
        }, 1100);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="booking-modal"
                className="max-w-[calc(100vw-2rem)] gap-0 overflow-hidden rounded-[2rem] border border-sand bg-cream p-0 sm:max-w-lg"
            >
                <div className="bg-forest px-7 pb-6 pt-7 text-cream">
                    <DialogHeader className="space-y-1.5 text-left">
                        <span className="eyebrow">Agendamento demonstrativo</span>
                        <DialogTitle className="font-serif text-2xl font-bold text-cream">
                            Reserve um momento de mimo
                        </DialogTitle>
                        <DialogDescription className="text-xs text-cream/70">
                            Demonstração de interface — nenhum agendamento real é registrado.
                        </DialogDescription>
                    </DialogHeader>
                </div>

                {sent ? (
                    <div className="flex flex-col items-center gap-3 px-7 py-10 text-center" data-testid="booking-success">
                        <span className="grid h-16 w-16 place-items-center rounded-full bg-coral/20">
                            <PartyPopper className="h-7 w-7 text-coral-dark" />
                        </span>
                        <p className="font-serif text-xl font-bold text-forest">Agendamento demonstrativo confirmado!</p>
                        <p className="max-w-sm text-sm text-graphite-muted">
                            Seu pet <strong className="text-forest">{petName}</strong> tem horário reservado para o
                            serviço selecionado em {date.split("-").reverse().join("/")} ({period === "manha" ? "manhã" : "tarde"}).
                            Nada foi registrado — este é um site de demonstração.
                        </p>
                        <button
                            type="button"
                            data-testid="booking-success-close"
                            onClick={() => onOpenChange(false)}
                            className="mt-2 rounded-full bg-forest px-6 py-3 text-sm font-bold text-cream transition-all hover:bg-forest-light"
                        >
                            Fechar
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="flex flex-col gap-4 px-7 py-6">
                        <div className="grid gap-4 sm:grid-cols-2">
                            <label className="flex flex-col gap-1.5 text-xs font-bold text-forest">
                                Nome do pet
                                <input
                                    type="text"
                                    value={petName}
                                    onChange={(e) => setPetName(e.target.value)}
                                    placeholder="Ex.: Mimo"
                                    data-testid="booking-pet-name-input"
                                    className="rounded-xl border border-sand bg-cream-card px-3.5 py-2.5 text-sm font-medium text-forest placeholder:text-forest/30 focus:border-coral focus:outline-none focus:ring-2 focus:ring-coral/30"
                                />
                            </label>
                            <div className="flex flex-col gap-1.5 text-xs font-bold text-forest">
                                Tipo de pet
                                <div className="grid grid-cols-2 gap-2">
                                    {[
                                        { id: "cao", label: "Cão" },
                                        { id: "gato", label: "Gato" },
                                    ].map((t) => (
                                        <button
                                            key={t.id}
                                            type="button"
                                            data-testid={`booking-pet-type-${t.id}`}
                                            onClick={() => setPetType(t.id)}
                                            className={`rounded-xl border px-3 py-2.5 text-sm font-bold transition-all ${
                                                petType === t.id
                                                    ? "border-forest bg-forest text-cream"
                                                    : "border-sand bg-cream-card text-forest/70 hover:border-coral"
                                            }`}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <label className="flex flex-col gap-1.5 text-xs font-bold text-forest">
                            Serviço
                            <select
                                value={serviceId}
                                onChange={(e) => setServiceId(e.target.value)}
                                data-testid="booking-service-select"
                                className="rounded-xl border border-sand bg-cream-card px-3.5 py-2.5 text-sm font-medium text-forest focus:border-coral focus:outline-none focus:ring-2 focus:ring-coral/30"
                            >
                                {services.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.title}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <div className="grid gap-4 sm:grid-cols-2">
                            <label className="flex flex-col gap-1.5 text-xs font-bold text-forest">
                                Data
                                <input
                                    type="date"
                                    min={today}
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    data-testid="booking-date-input"
                                    className="rounded-xl border border-sand bg-cream-card px-3.5 py-2.5 text-sm font-medium text-forest focus:border-coral focus:outline-none focus:ring-2 focus:ring-coral/30"
                                />
                            </label>
                            <div className="flex flex-col gap-1.5 text-xs font-bold text-forest">
                                Período
                                <div className="grid grid-cols-2 gap-2">
                                    {PERIODS.map((p) => (
                                        <button
                                            key={p.id}
                                            type="button"
                                            data-testid={`booking-period-${p.id}`}
                                            onClick={() => setPeriod(p.id)}
                                            className={`rounded-xl border px-3 py-2.5 text-sm font-bold transition-all ${
                                                period === p.id
                                                    ? "border-coral bg-coral/20 text-forest"
                                                    : "border-sand bg-cream-card text-forest/70 hover:border-coral"
                                            }`}
                                        >
                                            {p.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {error && (
                            <p
                                role="alert"
                                data-testid="booking-error"
                                className="flex items-center gap-2 rounded-xl bg-destructive/10 px-3.5 py-2.5 text-xs font-semibold text-destructive"
                            >
                                <PawPrint className="h-3.5 w-3.5" />
                                {error}
                            </p>
                        )}

                        <button
                            type="submit"
                            disabled={sending}
                            data-testid="booking-modal-submit"
                            className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-full bg-forest py-3.5 text-sm font-bold text-cream transition-all duration-300 hover:bg-forest-light disabled:opacity-70"
                        >
                            {sending ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Confirmando...
                                </>
                            ) : (
                                <>
                                    <Check className="h-4 w-4" />
                                    Confirmar agendamento (demo)
                                </>
                            )}
                        </button>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
